/**
 * Phase 26 — Public-Facing Partner Explanation page.
 *
 * General-explanation page only. NOT an individual partner SEO page.
 * Content is fetched from /api/pws/partner-network/explanation, so the
 * Phase 25 consent catalogue stays the single source of truth.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, FlaskConical, MapPin, HelpCircle,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import api from "@/lib/api";


export default function PartnerNetwork() {
  useSEO({
    title:
      "How the Aware Partner Network Works — Local Support, Aware Testing",
    description:
      "Aware Water Testing performs every water test and issues the Certificate of Analysis. Local Pristine Water Systems partners may help with sample support, follow-up recommendations and corrective service discussions where appropriate. Recommendations are not quotes; no service work is carried out without your approval.",
    url: `${SEO_DEFAULTS.baseUrl}/partner-network`,
    type: "website",
  });

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api
      .get("/pws/partner-network/explanation")
      .then((r) => {
        if (live) setData(r.data);
      })
      .catch((e) => {
        if (live) setError(e?.message || "failed to load");
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div data-testid="ph26-partner-network-page" className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-28 lg:pt-36 pb-14 lg:pb-20 overflow-hidden">
          <div className="absolute top-32 right-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl -z-10" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <span
                data-testid="ph26-eyebrow"
                className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600"
              >
                Partner Network
              </span>
              <h1
                data-testid="ph26-page-title"
                className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]"
              >
                {data?.title || "How the Aware Partner Network Works"}
              </h1>
              <p
                data-testid="ph26-page-subtitle"
                className="mt-6 text-lg text-slate-700 leading-relaxed"
              >
                {data?.subtitle ||
                  "Testing by Aware Water Testing. Local support by Pristine Water Systems."}
              </p>
              <p
                data-testid="ph26-page-lead"
                className="mt-4 text-base text-slate-600 leading-relaxed"
              >
                {data?.lead ||
                  "Aware Water Testing performs every water test and issues the Certificate of Analysis. Booking through a partner does not change the testing process."}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Sections */}
        <section className="py-10 lg:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            {error && (
              <div
                data-testid="ph26-error"
                className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
              >
                We couldn&apos;t load the latest content. Showing the standard
                explanation below.
              </div>
            )}
            {(data?.sections || FALLBACK_SECTIONS).map((s, i) => (
              <motion.section
                key={s.slug}
                data-testid={`ph26-section-${s.slug}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
              >
                <div className="flex items-center gap-3">
                  <SectionIcon slug={s.slug} />
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {s.title}
                  </h2>
                </div>
                <ul className="mt-5 space-y-3 text-base text-slate-700 leading-relaxed">
                  {s.body.map((line, idx) => (
                    <li
                      key={idx}
                      data-testid={`ph26-section-${s.slug}-line-${idx}`}
                      className="flex gap-2"
                    >
                      <span className="mt-2.5 inline-block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </motion.section>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-slate-50 py-14 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-7 h-7 text-cyan-600" />
              <h2
                data-testid="ph26-faqs-title"
                className="font-display text-2xl sm:text-3xl font-bold text-slate-900"
              >
                Common questions
              </h2>
            </div>
            <div
              data-testid="ph26-faqs"
              className="mt-7 grid gap-4 sm:grid-cols-2"
            >
              {(data?.faqs || FALLBACK_FAQS).map((f, i) => (
                <div
                  key={i}
                  data-testid={`ph26-faq-${i}`}
                  className="rounded-2xl bg-white border border-slate-200 p-5"
                >
                  <div className="text-base font-semibold text-slate-900">
                    {f.question}
                  </div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {f.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing reassurance + CTA */}
        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-[2.5rem] bg-slate-900 text-white p-10 sm:p-14 lg:p-20 overflow-hidden">
              <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyan-500/25 rounded-full blur-3xl" />
              <div className="relative max-w-2xl">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">
                  Book directly with Aware or through a partner
                </span>
                <h2 className="font-display mt-3 text-3xl sm:text-5xl font-bold leading-[1.05]">
                  Same lab. Same standards.
                </h2>
                <p className="mt-4 text-slate-300 text-lg max-w-xl">
                  Whether you book directly with us or arrive through a
                  partner link, the water testing is performed by
                  Aware Water Testing and the Certificate of
                  Analysis is issued by Aware.
                </p>
                <Link
                  data-testid="ph26-cta-book"
                  to="/book-water-test"
                  className="btn-aqua mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold"
                >
                  Book a water test <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}


function SectionIcon({ slug }) {
  if (slug === "how-it-works") return <FlaskConical className="w-7 h-7 text-cyan-600" />;
  if (slug === "testing-by-scwt-local-support-by-pws") return <FlaskConical className="w-7 h-7 text-cyan-600" />;
  if (slug === "what-customers-receive") return <ShieldCheck className="w-7 h-7 text-cyan-600" />;
  if (slug === "what-partners-may-help-with") return <MapPin className="w-7 h-7 text-cyan-600" />;
  if (slug === "what-partners-do-not-do") return <ShieldCheck className="w-7 h-7 text-cyan-600" />;
  if (slug === "booking-through-a-partner-link") return <MapPin className="w-7 h-7 text-cyan-600" />;
  return <FlaskConical className="w-7 h-7 text-cyan-600" />;
}


// Fallback content — mirrors the backend so the page is still useful
// even if the API call fails. The backend remains the single source of
// truth; this is purely a defence-in-depth render.
const FALLBACK_SECTIONS = [
  {
    slug: "how-it-works",
    title: "How the Partner Network Works",
    body: [
      "Aware Water Testing performs every water test and issues the Certificate of Analysis.",
      "Where a Pristine Water Systems partner operates in your area, they may provide local support — sample guidance, booking support, follow-up recommendations and corrective service discussions where appropriate.",
      "Booking through a partner does not change the testing process or the Certificate of Analysis you receive.",
    ],
  },
  {
    slug: "testing-by-scwt-local-support-by-pws",
    title: "Testing by Aware, local support by PWS",
    body: [
      "Water testing is performed by Aware Water Testing. Local support may be provided by participating Pristine Water Systems partners.",
      "The local PWS partner is not the laboratory. They do not issue the Certificate of Analysis.",
    ],
  },
  {
    slug: "what-customers-receive",
    title: "What you receive",
    body: [
      "The water test package you selected.",
      "Your Certificate of Analysis issued by Aware Water Testing.",
      "An easy-to-read Summary Report explaining your results.",
      "Optional follow-up recommendations where the result indicates further attention.",
    ],
  },
  {
    slug: "what-partners-may-help-with",
    title: "What local PWS partners may help with",
    body: [
      "Sample support, explaining recommended next steps, tank cleaning discussions and filtration/UV options where appropriate.",
    ],
  },
  {
    slug: "what-partners-do-not-do",
    title: "What PWS partners do not do",
    body: [
      "The PWS partner is not the laboratory.",
      "The PWS partner does not issue the Certificate of Analysis.",
      "Recommendations are not quotes.",
      "No service work is carried out without your approval.",
    ],
  },
  {
    slug: "booking-through-a-partner-link",
    title: "Booking through a partner link",
    body: [
      "Booking through a partner does not change the testing process.",
      "It does not create any obligation to buy services.",
      "You can still book directly with Aware at any time.",
    ],
  },
];

const FALLBACK_FAQS = [
  { question: "Who performs the water test?", answer: "Aware Water Testing performs every water test and issues the Certificate of Analysis." },
  { question: "Is the PWS partner the laboratory?", answer: "No. The local Pristine Water Systems partner is not the laboratory." },
  { question: "Do I have to approve any recommended work?", answer: "Yes. No service work is carried out without your approval." },
  { question: "Does booking through a partner change my test result?", answer: "No. Booking through a partner does not change the testing process or the Certificate of Analysis." },
];
